import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Loader2, CheckCircle2, AlertCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const ADMIN_LOGO_URL = 'https://customer-assets.emergentagent.com/job_8c0fe4ee-111e-41b1-aff2-64466f27b787/artifacts/dwo3qstc_Stock%20%26%20Load%20Admin.png';

export function AdminAcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [state, setState] = useState('loading');
  const [inviteInfo, setInviteInfo] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!token) {
      setState('error');
      setErrorMsg('No invitation token provided.');
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/admin/team/invite-validate/${token}`);
        setInviteInfo(res.data);
        setName(res.data.name || '');
        setState('valid');
      } catch (err) {
        setState('error');
        setErrorMsg(err.response?.data?.detail || 'This invitation is invalid or has expired.');
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!name.trim()) { setFormError('Please enter your name.'); return; }
    if (password.length < 8) { setFormError('Password must be at least 8 characters.'); return; }
    if (password !== confirmPassword) { setFormError('Passwords do not match.'); return; }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE}/api/admin/team/accept-invite`, {
        token, name: name.trim(), password,
      });
      if (res.data.access_token) {
        localStorage.setItem('admin_token', res.data.access_token);
      }
      setState('success');
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Failed to accept invitation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md" data-testid="accept-invite-page">
        {/* Logo/Header — uses the custom admin logo */}
        <div className="text-center mb-8">
          <img
            src={ADMIN_LOGO_URL}
            alt="Stock & Load Admin"
            className="h-16 w-auto object-contain mx-auto mb-3"
            data-testid="accept-invite-logo"
          />
          <p className="text-sm text-slate-400 mt-1">Admin Portal</p>
        </div>

        {/* Loading */}
        {state === 'loading' && (
          <Card className="bg-slate-800/60 border-slate-700" data-testid="invite-loading">
            <CardContent className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-blue-400 mx-auto mb-3" />
              <p className="text-slate-300 text-sm">Validating your invitation...</p>
            </CardContent>
          </Card>
        )}

        {/* Error */}
        {state === 'error' && (
          <Card className="bg-slate-800/60 border-red-500/30" data-testid="invite-error">
            <CardContent className="p-8 text-center">
              <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-6 h-6 text-red-400" />
              </div>
              <h2 className="text-lg font-semibold text-white mb-2">Invitation Invalid</h2>
              <p className="text-sm text-slate-400 mb-6">{errorMsg}</p>
              <Button
                onClick={() => navigate('/admin/login')}
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
                data-testid="invite-error-login-btn"
              >
                Go to Admin Login
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Valid — Setup Form */}
        {state === 'valid' && inviteInfo && (
          <Card className="bg-slate-800/60 border-slate-700" data-testid="invite-setup-form">
            <CardContent className="p-6 sm:p-8">
              <div className="text-center mb-6">
                <h2 className="text-lg font-semibold text-white">Welcome to the Team</h2>
                <p className="text-sm text-slate-400 mt-1">
                  You've been invited by <span className="text-slate-200 font-medium">{inviteInfo.invited_by}</span>
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300 text-sm">Email</Label>
                  <Input
                    type="email"
                    value={inviteInfo.email}
                    disabled
                    className="bg-slate-700/40 border-slate-600 text-slate-400"
                    data-testid="accept-email-display"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300 text-sm">Your Name</Label>
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your full name"
                    className="bg-slate-700/40 border-slate-600 text-white placeholder:text-slate-500"
                    required
                    data-testid="accept-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300 text-sm">Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Min. 8 characters"
                      className="bg-slate-700/40 border-slate-600 text-white placeholder:text-slate-500 pr-10"
                      required
                      data-testid="accept-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300 text-sm">Confirm Password</Label>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter password"
                    className="bg-slate-700/40 border-slate-600 text-white placeholder:text-slate-500"
                    required
                    data-testid="accept-confirm-password-input"
                  />
                </div>

                {formError && (
                  <p className="text-sm text-red-400 flex items-center gap-1.5" data-testid="accept-form-error">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                    {formError}
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-blue-600 hover:bg-blue-500 text-white mt-2"
                  data-testid="accept-submit-btn"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Setting up...</>
                  ) : (
                    'Create Account & Sign In'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Success */}
        {state === 'success' && (
          <Card className="bg-slate-800/60 border-emerald-500/30" data-testid="invite-success">
            <CardContent className="p-8 text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-400" />
              </div>
              <h2 className="text-lg font-semibold text-white mb-2">You're In!</h2>
              <p className="text-sm text-slate-400 mb-6">
                Your admin account has been created. You can now access the admin portal.
              </p>
              <Button
                onClick={() => navigate('/admin/dashboard')}
                className="bg-blue-600 hover:bg-blue-500 text-white"
                data-testid="invite-success-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default AdminAcceptInvite;
